/**
 * Unified sign-in: this app's /auth/signin is the one sign-in page for Re:Loam.
 * Landlord-side roles stay here; role `tenant` is handed to the tenant app.
 *
 * The two apps live on different subdomains, so storage is not shared. The tenant
 * hand-off asks the API for a single-use, 60-second code (POST /public/auth/handoff,
 * authorised with the token the user already holds) and puts only that code in the URL
 * fragment. The tenant app exchanges it for a session and scrubs the URL on arrival.
 * The access token itself is never placed in a URL.
 *
 * Everything here is pure (no DOM writes) except navigateExternal, so it is easy to test.
 * Redirect targets come only from config and are checked against an allow-list; a query
 * parameter can select between known targets but can never supply one.
 */

export const DEFAULT_TENANT_ORIGIN = 'https://tn.reloam.ainaeco.uk';

/** The only https origins the tenant hand-off may ever point at. */
const ALLOWED_TENANT_ORIGINS: readonly string[] = [DEFAULT_TENANT_ORIGIN, 'https://demo-tenant.ainaeco.uk'];

const LOOPBACK_HOSTS: readonly string[] = ['localhost', '127.0.0.1', '[::1]'];
const MAX_TOKEN_LENGTH = 4096;
const TOKEN_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export const HANDOFF_LOG_KEY = 'reloam:handoffs';
const HANDOFF_WINDOW_MS = 60_000;
const HANDOFF_MAX_IN_WINDOW = 3;

const isLoopback = (hostname: string): boolean => LOOPBACK_HOSTS.includes(hostname.toLowerCase());

/**
 * Validate the configured tenant app URL and return its origin, or null when it is not allowed.
 * Empty/undefined means "use the default". Loopback targets are only allowed when this page
 * itself is served from loopback (local development), never on a real host.
 */
export function resolveTenantOrigin(configured: string | undefined, currentHostname: string): string | null {
  const raw = (configured ?? '').trim();
  if (!raw) return DEFAULT_TENANT_ORIGIN;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.username || url.password) return null;

  const origin = url.origin; // lower-cased scheme + host (+ port)
  if (url.protocol === 'https:') {
    return ALLOWED_TENANT_ORIGINS.includes(origin) ? origin : null;
  }
  if (url.protocol === 'http:' && isLoopback(url.hostname) && isLoopback(currentHostname)) {
    return origin;
  }
  return null;
}

/** True for something shaped like a JWT (three base64url segments) and not absurdly long. */
export function isWellFormedToken(token: unknown): token is string {
  return typeof token === 'string' && token.length <= MAX_TOKEN_LENGTH && TOKEN_SHAPE.test(token);
}

/** Decode a JWT payload without verifying it (routing hint only; the server enforces access). */
export function jwtPayload(token: string): Record<string, unknown> | null {
  if (!isWellFormedToken(token)) return null;
  try {
    const seg = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const padded = seg + '='.repeat((4 - (seg.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

const normaliseRole = (r: unknown): string | null => (typeof r === 'string' && r.trim() ? r.trim().toLowerCase() : null);

/** Role from the JWT `role` claim, falling back to the login response's user.role. */
export function resolveRole(token: string, userRole?: string | null): string | null {
  return normaliseRole(jwtPayload(token)?.['role']) ?? normaliseRole(userRole);
}

export type Destination = { kind: 'landlord' } | { kind: 'tenant' };

/**
 * Only `tenant` leaves this app. Every other role (admin/manager/user, or anything new)
 * stays, so nobody who can sign in today is ever locked out.
 */
export function decideDestination(role: string | null | undefined): Destination {
  return normaliseRole(role) === 'tenant' ? { kind: 'tenant' } : { kind: 'landlord' };
}

/** `?from=tenant` selects a known target. Any other value (URLs, paths) is ignored. */
export function parseFromParam(search: string): 'tenant' | null {
  const from = new URLSearchParams(search).get('from');
  return from !== null && from.trim().toLowerCase() === 'tenant' ? 'tenant' : null;
}

/** The tenant app's original sign-in page. `direct=1` stops it forwarding back here. */
export function tenantDirectLoginUrl(tenantOrigin: string): string {
  return `${tenantOrigin}/login?direct=1`;
}

const CODE_SHAPE = /^[A-Za-z0-9._~=-]{8,512}$/; // base64url codes from the API end in "=" padding

/** True for something shaped like a one-time hand-off code (opaque, URL-safe, bounded). */
export function isWellFormedCode(code: unknown): code is string {
  return typeof code === 'string' && CODE_SHAPE.test(code);
}

/**
 * Build the tenant hand-off URL. It carries a single-use, 60-second code in the fragment
 * (never the access token), which the tenant app exchanges with the API.
 */
export function buildTenantHandoffUrl(tenantOrigin: string, code: string): string {
  if (!isWellFormedCode(code)) throw new Error('Refusing to hand off a malformed code');
  return `${tenantOrigin}/auth/handoff#code=${encodeURIComponent(code)}`;
}

export const BLOCKED_MESSAGES = {
  config:
    'Your account is a tenant account, but the tenant portal address is not set up correctly on this site. Please contact reloam@ainaeco.uk.',
  loop: 'Your account is a tenant account. Please open the tenant portal directly instead of signing in here.',
  token: 'Your sign-in could not be completed. Please try again.',
  looping:
    'We keep sending you between the two portals. Please use the tenant sign-in link below, or contact reloam@ainaeco.uk.',
  unavailable:
    'Your account is a tenant account, but we could not take you to the tenant portal automatically. Please use the tenant sign-in.',
} as const;

export type PostLoginPlan =
  | { action: 'stay' }
  | { action: 'handoff'; origin: string }
  | { action: 'blocked'; reason: 'config' | 'loop' | 'token'; message: string };

interface PlanInput {
  token: string;
  userRole?: string | null;
  tenantAppUrl: string | undefined;
  currentOrigin: string;
  currentHostname: string;
}

/** Decide what to do right after a successful login. Never throws. */
export function planPostLogin(input: PlanInput): PostLoginPlan {
  const role = resolveRole(input.token, input.userRole);
  if (decideDestination(role).kind !== 'tenant') return { action: 'stay' };

  const origin = resolveTenantOrigin(input.tenantAppUrl, input.currentHostname);
  if (!origin) return { action: 'blocked', reason: 'config', message: BLOCKED_MESSAGES.config };
  // Handing a tenant to ourselves would bounce them forever.
  if (origin === input.currentOrigin) return { action: 'blocked', reason: 'loop', message: BLOCKED_MESSAGES.loop };
  if (!isWellFormedToken(input.token)) return { action: 'blocked', reason: 'token', message: BLOCKED_MESSAGES.token };

  return { action: 'handoff', origin };
}

/**
 * Loop protection: allow at most a few hand-offs per minute per tab. Returns false when the
 * caller should stop and show a message instead of redirecting again. Fails open if storage
 * is unavailable (a broken sessionStorage must never block sign-in).
 */
export function recordHandoffAttempt(storage: Pick<Storage, 'getItem' | 'setItem'>, now: number = Date.now()): boolean {
  try {
    const parsed = JSON.parse(storage.getItem(HANDOFF_LOG_KEY) ?? '[]');
    const recent: number[] = (Array.isArray(parsed) ? parsed : []).filter(
      (t): t is number => typeof t === 'number' && now - t < HANDOFF_WINDOW_MS,
    );
    if (recent.length >= HANDOFF_MAX_IN_WINDOW) return false;
    storage.setItem(HANDOFF_LOG_KEY, JSON.stringify([...recent, now]));
    return true;
  } catch {
    return true;
  }
}

/** Leave this app. Wrapped so tests can intercept the navigation. */
export function navigateExternal(url: string): void {
  window.location.assign(url);
}

/** Tenant app origin for this build/page, or null if the configured value is not allowed. */
export function configuredTenantOrigin(): string | null {
  return resolveTenantOrigin(import.meta.env.VITE_TENANT_APP_URL, window.location.hostname);
}
